import { memo } from 'react';

interface RevealAnimationProps {
  /** Starting X position of the clip rect */
  startX: number;
  /** Full width of the clip rect */
  width: number;
  /** Animation duration in seconds (default: 2.5) */
  duration?: number;
  /** Delay before animation starts in seconds (default: 0) */
  delay?: number;
}

/**
 * SVG animation for clip-path reveal and hide effect.
 * Animates a rect's x position to reveal then hide the underlying path.
 *
 * Animation: rect slides from left (hidden) to center (visible) to right (hidden).
 * Both reveal and hide directions are left-to-right.
 */
function RevealAnimation({
  startX,
  width,
  duration = 2.5,
  delay = 0,
}: RevealAnimationProps) {
  // Animation positions:
  // hideLeft: rect is completely to the left (content hidden)
  // showPosition: rect covers the content (content visible)
  // hideRight: rect is completely to the right (content hidden)
  const hideLeft = startX - width;
  const showPosition = startX;
  const hideRight = startX + width;

  return (
    <animate
      attributeName="x"
      values={`${hideLeft}; ${showPosition}; ${showPosition}; ${hideRight}`}
      keyTimes="0; 0.4; 0.5; 1"
      dur={`${duration}s`}
      begin={`${delay}s`}
      repeatCount="indefinite"
      calcMode="spline"
      keySplines="0.4 0 0.2 1; 0.5 0 0.5 1; 0.4 0 0.2 1"
    />
  );
}

export default memo(RevealAnimation);
